import {
  type AuthResponse,
  type FinishSpeakingSessionResponse,
  LeaderboardMeSchema,
  type LeaderboardResponse,
  LeaderboardRowSchema,
  type SpeakingHistoryResponse,
  type SpeakingResults,
  type StartSpeakingSessionRequest,
  type StartSpeakingSessionResponse,
  type SubmitTurnResponse,
  type UserProfile,
} from '@ielts-buddy/shared'

// Empty = same-origin. In `pnpm dev`, Vite proxies `/auth`, `/speaking`, etc. to
// `VITE_API_PROXY_TARGET` (see vite.config.ts) so one tunnel can reach both web
// and a local API. Set `VITE_API_URL` when the API lives on another origin
// (e.g. `http://127.0.0.1:3001` or a second tunnel URL).
const rawApiUrl = import.meta.env.VITE_API_URL
const API_URL =
  typeof rawApiUrl === 'string' && rawApiUrl.trim().length > 0
    ? rawApiUrl.trim().replace(/\/+$/, '')
    : ''

const rawPathPrefix = import.meta.env.VITE_API_PATH_PREFIX
const API_PATH_PREFIX =
  typeof rawPathPrefix === 'string' && rawPathPrefix.trim().length > 0
    ? rawPathPrefix.trim().replace(/^\/+|\/+$/g, '')
    : ''

function apiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const prefix = API_PATH_PREFIX ? `/${API_PATH_PREFIX}` : ''
  return `${API_URL}${prefix}${normalized}`
}

const TOKEN_KEY = 'ielts-buddy:token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  // Auto-set JSON content-type for plain bodies, but never for FormData —
  // the browser must set its own multipart boundary.
  const isFormData =
    typeof FormData !== 'undefined' && init.body instanceof FormData
  if (!isFormData && !headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  const token = getToken()
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(apiUrl(path), { ...init, headers })
  const text = await res.text()
  const contentType = res.headers.get('content-type') ?? ''
  const declaredJson = contentType.includes('application/json')
  const trimmed = text.trim()
  const looksLikeJson =
    declaredJson || trimmed.startsWith('{') || trimmed.startsWith('[')
  let payload: unknown = text
  if (looksLikeJson) {
    try {
      payload = JSON.parse(text) as unknown
    } catch {
      payload = text
    }
  }

  if (!res.ok) {
    const message =
      (typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null) ?? `Request failed with status ${res.status}`
    throw new ApiError(message, res.status, payload)
  }

  return payload as T
}

/**
 * One in-flight `/speaking/history` or `/leaderboard` request at a time (per tab).
 * React StrictMode double-mounts tab screens in dev, and quick BottomNav
 * switches can overlap fetches; the API on Cloudflare Workers has shown
 * cross-request I/O errors when those GETs hit the same isolate concurrently.
 */
let resultsLeaderboardQueue: Promise<void> = Promise.resolve()

function runResultsLeaderboardSerialized<T>(fn: () => Promise<T>): Promise<T> {
  const p = resultsLeaderboardQueue.then(fn, fn)
  resultsLeaderboardQueue = p.then(
    () => undefined,
    () => undefined,
  )
  return p
}

export async function loginWithTelegram(
  initData: string,
): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ initData }),
  })
}

export async function fetchMe(): Promise<UserProfile> {
  return request<UserProfile>('/user/me')
}

export async function syncUserFromTelegram(
  initData: string,
): Promise<UserProfile> {
  return request<UserProfile>('/user/sync-telegram', {
    method: 'POST',
    body: JSON.stringify({ initData }),
  })
}

export async function deleteUserMe(): Promise<void> {
  await request<{ ok: true }>('/user/me', { method: 'DELETE' })
}

// ---------------------------------------------------------------------------
// Speaking module
// ---------------------------------------------------------------------------

export async function startSpeakingSession(
  params: StartSpeakingSessionRequest = { mode: 'full_test' },
): Promise<StartSpeakingSessionResponse> {
  return request<StartSpeakingSessionResponse>('/speaking/sessions', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/**
 * DEV-ONLY: skip the current speaking turn with a canned answer. Backend
 * rejects with 403 if `DEV_BYPASS_TELEGRAM_AUTH` isn't enabled, so this is
 * safe to leave in production builds — it just won't work.
 */
export async function skipSpeakingTurn(
  sessionId: string,
): Promise<SubmitTurnResponse> {
  return request<SubmitTurnResponse>(
    `/speaking/sessions/${sessionId}/skip-turn`,
    {
      method: 'POST',
    },
  )
}

export async function submitSpeakingTurn(
  sessionId: string,
  audio: Blob,
): Promise<SubmitTurnResponse> {
  // Use multipart upload — Whisper-friendly and avoids base64 bloat.
  const form = new FormData()
  // Filename hint helps backends infer the codec.
  const ext = audio.type.includes('mp4')
    ? 'mp4'
    : audio.type.includes('mpeg')
      ? 'mp3'
      : 'webm'
  form.append('audio', audio, `turn.${ext}`)
  return request<SubmitTurnResponse>(`/speaking/sessions/${sessionId}/turn`, {
    method: 'POST',
    body: form,
  })
}

export async function finishSpeakingSession(
  sessionId: string,
): Promise<FinishSpeakingSessionResponse> {
  return request<FinishSpeakingSessionResponse>(
    `/speaking/sessions/${sessionId}/finish`,
    { method: 'POST' },
  )
}

export async function fetchSpeakingResults(
  sessionId: string,
): Promise<SpeakingResults> {
  return request<SpeakingResults>(`/speaking/sessions/${sessionId}`)
}

export async function fetchSpeakingHistory(): Promise<SpeakingHistoryResponse> {
  return runResultsLeaderboardSerialized(async () => {
    const payload = await request<unknown>('/speaking/history')
    if (
      typeof payload !== 'object' ||
      payload === null ||
      !('sessions' in payload) ||
      !Array.isArray((payload as { sessions: unknown }).sessions)
    ) {
      const hint =
        typeof payload === 'string' &&
        (payload.includes('<!DOCTYPE') || payload.includes('<html'))
          ? ' Received HTML — set VITE_API_URL to the API origin.'
          : ''
      throw new ApiError(
        `Invalid speaking history response.${hint}`,
        502,
        payload,
      )
    }
    return payload as SpeakingHistoryResponse
  })
}

function toFiniteNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** Postgres `numeric` / driver quirks can stringify numbers; Zod expects real numbers. */
function coerceLeaderboardRowLike(row: unknown): unknown {
  if (typeof row !== 'object' || row === null) return row
  const r = { ...(row as Record<string, unknown>) }
  if ('rank' in r && r.rank !== null && r.rank !== undefined) {
    const rank = toFiniteNumber(r.rank)
    if (rank !== null) r.rank = Math.trunc(rank)
  }
  if (
    'sessionsScored' in r &&
    r.sessionsScored !== null &&
    r.sessionsScored !== undefined
  ) {
    const sessions = toFiniteNumber(r.sessionsScored)
    if (sessions !== null) r.sessionsScored = Math.max(0, Math.trunc(sessions))
  }
  if ('avgBand' in r && r.avgBand !== null && r.avgBand !== undefined) {
    const band = toFiniteNumber(r.avgBand)
    if (band !== null) r.avgBand = Math.round(band * 2) / 2
  }
  if (!('photoUrl' in r) || r.photoUrl === undefined) {
    r.photoUrl = null
  } else if (r.photoUrl !== null && typeof r.photoUrl !== 'string') {
    r.photoUrl = null
  }
  return r
}

export async function fetchLeaderboard(): Promise<LeaderboardResponse> {
  return runResultsLeaderboardSerialized(async () => {
    const payload = await request<unknown>('/leaderboard')
    if (typeof payload !== 'object' || payload === null) {
      const hint =
        typeof payload === 'string' &&
        (payload.includes('<!DOCTYPE') || payload.includes('<html'))
          ? ' Received HTML — check VITE_API_URL points to the API, not the Mini App host.'
          : ''
      throw new ApiError(`Invalid leaderboard response.${hint}`, 502, payload)
    }

    const raw = payload as Record<string, unknown>
    const rowsRaw = raw.rows
    const rowsIn = Array.isArray(rowsRaw) ? rowsRaw : []

    const rows: LeaderboardResponse['rows'] = []
    for (const item of rowsIn) {
      const parsed = LeaderboardRowSchema.safeParse(
        coerceLeaderboardRowLike(item),
      )
      if (parsed.success) rows.push(parsed.data)
    }

    const meRaw =
      raw.me !== null && typeof raw.me === 'object' && !Array.isArray(raw.me)
        ? coerceLeaderboardRowLike(raw.me)
        : { rank: null, avgBand: null, sessionsScored: 0 }

    const meParsed = LeaderboardMeSchema.safeParse(meRaw)
    const me: LeaderboardResponse['me'] = meParsed.success
      ? meParsed.data
      : { rank: null, avgBand: null, sessionsScored: 0 }

    return { rows, me }
  })
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export async function submitFeedback(params: {
  rating: number
  comment?: string
  sessionId?: string
  context?: string
}): Promise<void> {
  await request<{ ok: true }>('/feedback', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

export { ApiError }
